import { useState } from "react";
import { AnimatePresence, m } from "framer-motion";

import { useToast } from "@/components/ui/toast";
import { useModalA11y } from "@/hooks/useModalA11y";
import type { EditConflictPayload } from "@/stores/editConflict";
import { useEditConflictStore } from "@/stores/editConflict";

/** Turn `acceptance_criteria` into `Acceptance criteria` for display. */
function humanizeField(key: string): string {
  const spaced = key.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function renderValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}

function EditConflictBody({ conflict }: { conflict: EditConflictPayload }) {
  const dismiss = useEditConflictStore((s) => s.dismiss);
  const toast = useToast();
  const [copied, setCopied] = useState(false);

  const changes = Object.entries(conflict.your_changes ?? {});

  async function copyChanges() {
    const text = changes
      .map(([k, v]) => `${humanizeField(k)}:\n${renderValue(v)}`)
      .join("\n\n");
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      // Not a toast-only confirmation: clipboard is the ONLY copy of their work
      // at this point, so the button itself has to say it worked.
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy — select the text below and copy it manually.");
    }
  }

  return (
    <div className="p-6">
      <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
        Someone else saved first
      </h2>
      <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{conflict.message}</p>
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
        You were editing version {conflict.your_version}; it is now version{" "}
        {conflict.current_version}. Your changes were not saved.
      </p>

      {changes.length > 0 && (
        <div className="mt-4 rounded-xl bg-slate-50 p-4 dark:bg-slate-800/60">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Your unsaved changes
          </p>
          <dl className="mt-2 space-y-3">
            {changes.map(([key, value]) => (
              <div key={key}>
                <dt className="text-xs text-slate-500 dark:text-slate-400">
                  {humanizeField(key)}
                </dt>
                <dd className="mt-0.5 whitespace-pre-wrap break-words text-sm text-slate-900 dark:text-slate-100">
                  {renderValue(value)}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
        {changes.length > 0 && (
          <button
            type="button"
            onClick={copyChanges}
            className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            {copied ? "Copied" : "Copy my changes"}
          </button>
        )}
        <button
          type="button"
          onClick={dismiss}
          className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
        >
          Keep editing
        </button>
        <button
          type="button"
          onClick={() => {
            dismiss();
            window.location.reload();
          }}
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
        >
          Reload the latest version
        </button>
      </div>
    </div>
  );
}

/**
 * App-level modal for a refused write on a record someone else already changed.
 *
 * Driven by useEditConflictStore, which the HTTP client populates on a 409
 * carrying `kind: "edit_conflict"`. It ships in the SAME change as the backend
 * guard that produces that 409, deliberately: without a surface, the guard would
 * trade a silently lost edit for a raw error blob, which is worse.
 *
 * "Reload" is offered rather than performed. Reloading for them would discard
 * the text they just typed, which the dialog is showing them precisely because
 * it is now the only copy.
 */
export function EditConflictModal() {
  const conflict = useEditConflictStore((s) => s.conflict);
  const dismiss = useEditConflictStore((s) => s.dismiss);

  const panelRef = useModalA11y<HTMLDivElement>({ open: !!conflict, onClose: dismiss });

  return (
    <AnimatePresence>
      {conflict && (
        <m.div
          key="edit-conflict-backdrop"
          className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={dismiss}
        >
          <m.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label="Someone else saved first"
            tabIndex={-1}
            className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white shadow-2xl dark:bg-slate-900"
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 16 }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
          >
            <EditConflictBody conflict={conflict} />
          </m.div>
        </m.div>
      )}
    </AnimatePresence>
  );
}
